// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import ScreenSharePublicationMigration from '@app/features/voice/engine/ScreenSharePublicationMigration';
import {
	createVoiceSubscriptionSnapshot,
	transitionVoiceSubscriptionSnapshot,
	type VideoQualityLevel,
	type VoiceRemoteTrackSubscriptionController,
	type VoiceSubscriptionCommand,
	type VoiceSubscriptionContext,
	type VoiceSubscriptionEvent,
	type VoiceSubscriptionSnapshot,
} from '@app/features/voice/engine/VoiceSubscriptionStateMachine';
import {asVoiceTrackSource, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import type {RemoteParticipant, RemoteTrackPublication, Room} from 'livekit-client';
import {VideoQuality} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('ScreenShareSubscriptionManager');

const qualityMap: Record<VideoQualityLevel, VideoQuality> = {
	low: VideoQuality.LOW,
	medium: VideoQuality.MEDIUM,
	high: VideoQuality.HIGH,
};

export class ScreenShareSubscriptionManager {
	private room: Room | null = null;
	private nativeController: VoiceRemoteTrackSubscriptionController | null = null;
	private snapshot: VoiceSubscriptionSnapshot = createVoiceSubscriptionSnapshot();
	private observers = new Map<string, IntersectionObserver>();
	private readonly intersectionOptions: IntersectionObserverInit = {
		root: null,
		rootMargin: '50px',
		threshold: [0, 0.1],
	};

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	setRoom(room: Room | null): void {
		this.room = room;
	}

	setNativeController(controller: VoiceRemoteTrackSubscriptionController | null): void {
		this.nativeController = controller;
	}

	cleanup(): void {
		this.transition({type: 'subscription.cleanup'});
	}

	subscribe(
		participantIdentity: string,
		element: HTMLElement | null,
		context: VoiceSubscriptionContext = 'carousel',
	): void {
		if (!this.room && !this.nativeController) {
			logger.warn('No room available');
			return;
		}
		const participant = this.room?.remoteParticipants.get(participantIdentity) ?? null;
		if (!participant) {
			if (!this.nativeController) {
				logger.warn('Participant not found', {participantIdentity});
				return;
			}
		}
		logger.info('Subscribing to screen share', {participantIdentity, context});
		const screenSharePublication = this.findScreenSharePublication(participant);
		if (!screenSharePublication && !this.nativeController) {
			logger.debug('No screen share publication found', {participantIdentity});
		}
		this.transition({
			type: 'subscription.subscribe',
			participantIdentity,
			hasPublication: this.nativeController != null || screenSharePublication != null,
			observedElement: element,
			context,
		});
		logger.debug('Screen share subscribe intent recorded', {participantIdentity, context});
	}

	unsubscribe(participantIdentity: string): void {
		if (!this.snapshot.context.entries.has(participantIdentity)) {
			logger.debug('Not subscribed', {participantIdentity});
			return;
		}
		logger.info('Unsubscribing from screen share', {participantIdentity});
		this.transition({type: 'subscription.unsubscribe', participantIdentity});
		logger.info('Screen share unsubscribed successfully', {participantIdentity});
	}

	setContext(participantIdentity: string, context: VoiceSubscriptionContext): void {
		const state = this.snapshot.context.entries.get(participantIdentity);
		if (!state) {
			logger.debug('Not subscribed', {participantIdentity});
			return;
		}
		if (state.context === context) {
			return;
		}
		logger.debug('Setting screen share context', {participantIdentity, context});
		this.transition({
			type: 'subscription.setContext',
			participantIdentity,
			hasPublication: this.hasScreenSharePublicationForIdentity(participantIdentity),
			context,
		});
	}

	isSubscribed(participantIdentity: string): boolean {
		return this.snapshot.context.entries.get(participantIdentity)?.subscribed ?? false;
	}

	getContext(participantIdentity: string): VoiceSubscriptionContext | null {
		return this.snapshot.context.entries.get(participantIdentity)?.context ?? null;
	}

	reattachAfterPublish(participantIdentity: string): void {
		const state = this.snapshot.context.entries.get(participantIdentity);
		if (!state?.subscribed || (!this.room && !this.nativeController)) return;
		logger.info('Reattaching screen share subscription after republish', {
			participantIdentity,
			context: state.context,
			enabled: state.enabled,
		});
		this.transition({
			type: 'subscription.reattachAfterPublish',
			participantIdentity,
			hasPublication: this.hasScreenSharePublicationForIdentity(participantIdentity),
		});
	}

	private findScreenSharePublication(
		participant: RemoteParticipant | null | undefined,
	): RemoteTrackPublication | undefined {
		if (!participant) return undefined;
		const selected = ScreenSharePublicationMigration.selectScreenSharePublication(participant);
		if (selected) return selected;
		for (const pub of participant.videoTrackPublications.values()) {
			if (asVoiceTrackSource(pub.source) === VoiceTrackSource.ScreenShare) {
				return pub;
			}
		}
		return undefined;
	}

	private findScreenSharePublicationForIdentity(participantIdentity: string): RemoteTrackPublication | undefined {
		return this.findScreenSharePublication(this.getParticipant(participantIdentity));
	}

	private hasScreenSharePublicationForIdentity(participantIdentity: string): boolean {
		return this.nativeController != null || this.findScreenSharePublicationForIdentity(participantIdentity) != null;
	}

	private applyQuality(publication: RemoteTrackPublication, quality: VideoQualityLevel): void {
		try {
			const lkQuality = qualityMap[quality];
			publication.setVideoQuality(lkQuality);
			logger.debug('Quality set', {quality, lkQuality});
		} catch (error) {
			logger.error('Failed to set quality', {quality, error});
		}
	}

	private getParticipant(participantIdentity: string): RemoteParticipant | null {
		return this.room?.remoteParticipants.get(participantIdentity) ?? null;
	}

	private getActiveScreenSharePublications(participant: RemoteParticipant | null): Array<RemoteTrackPublication> {
		const managedPublications = ScreenSharePublicationMigration.getManagedScreenSharePublications(participant);
		if (managedPublications.length > 0) return managedPublications;
		const fallbackPublication = this.findScreenSharePublication(participant);
		return fallbackPublication ? [fallbackPublication] : [];
	}

	private getAllScreenSharePublications(participant: RemoteParticipant | null): Array<RemoteTrackPublication> {
		if (!participant) return [];
		return Array.from(participant.videoTrackPublications.values()).filter(
			(publication): publication is RemoteTrackPublication =>
				asVoiceTrackSource(publication.source) === VoiceTrackSource.ScreenShare,
		);
	}

	private withActiveScreenSharePublications(
		participantIdentity: string,
		apply: (publications: Array<RemoteTrackPublication>, participant: RemoteParticipant | null) => void,
	): void {
		const participant = this.getParticipant(participantIdentity);
		const publications = this.getActiveScreenSharePublications(participant);
		if (publications.length === 0) {
			this.transition({type: 'subscription.publicationMissing', participantIdentity});
			return;
		}
		apply(publications, participant);
	}

	private applyToPublications(
		participantIdentity: string,
		publications: ReadonlyArray<RemoteTrackPublication>,
		apply: (publication: RemoteTrackPublication) => void,
		errorMessage: string,
	): void {
		for (const publication of publications) {
			try {
				apply(publication);
			} catch (error) {
				logger.error(errorMessage, {participantIdentity, trackSid: publication.trackSid, error});
			}
		}
	}

	private createObserver(participantIdentity: string, element: HTMLElement): IntersectionObserver {
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				const isIntersecting = entry.isIntersecting;
				if (!this.snapshot.context.entries.has(participantIdentity)) continue;
				this.transition({
					type: 'subscription.intersection',
					participantIdentity,
					hasPublication: this.findScreenSharePublicationForIdentity(participantIdentity) != null,
					isIntersecting,
				});
				logger.debug('Intersection changed', {participantIdentity, isIntersecting});
			}
		}, this.intersectionOptions);
		observer.observe(element);
		return observer;
	}

	private transition(event: VoiceSubscriptionEvent): void {
		runInAction(() => {
			this.snapshot = transitionVoiceSubscriptionSnapshot(this.snapshot, event);
		});
		const commands = [...this.snapshot.context.commands];
		runInAction(() => {
			this.snapshot = transitionVoiceSubscriptionSnapshot(this.snapshot, {type: 'subscription.clearCommands'});
		});
		for (const command of commands) {
			this.applyCommand(command);
		}
	}

	private applyCommand(command: VoiceSubscriptionCommand): void {
		switch (command.type) {
			case 'subscribePublication':
				this.subscribePublication(command.participantIdentity, command.enabled, command.quality);
				break;
			case 'unsubscribePublication':
				this.unsubscribePublication(command.participantIdentity);
				break;
			case 'setPublicationEnabled':
				this.setPublicationEnabled(command.participantIdentity, command.enabled);
				break;
			case 'setPublicationQuality':
				this.setPublicationQuality(command.participantIdentity, command.quality);
				break;
			case 'observeElement':
				this.observers.set(
					command.participantIdentity,
					this.createObserver(command.participantIdentity, command.element as HTMLElement),
				);
				break;
			case 'disconnectObserver':
				this.observers.get(command.participantIdentity)?.disconnect();
				this.observers.delete(command.participantIdentity);
				break;
		}
	}

	private subscribePublication(participantIdentity: string, enabled: boolean, quality: VideoQualityLevel): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.ScreenShare,
				subscribed: true,
				enabled,
				quality,
			});
			return;
		}
		this.withActiveScreenSharePublications(participantIdentity, (publications, participant) => {
			this.applyToPublications(
				participantIdentity,
				publications,
				(publication) => {
					publication.setSubscribed(true);
					publication.setEnabled(enabled);
					this.applyQuality(publication, quality);
					logger.debug('Screen share subscribed successfully', {
						participantIdentity,
						trackSid: publication.trackSid,
					});
				},
				'Failed to subscribe',
			);
			this.applyToPublications(
				participantIdentity,
				ScreenSharePublicationMigration.getScreenSharePublicationsToDisable(participant),
				(publication) => publication.setSubscribed(false),
				'Failed to unsubscribe inactive migration publication',
			);
		});
	}

	private unsubscribePublication(participantIdentity: string): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.ScreenShare,
				subscribed: false,
				enabled: false,
			});
			return;
		}
		this.applyToPublications(
			participantIdentity,
			this.getAllScreenSharePublications(this.getParticipant(participantIdentity)),
			(publication) => {
				publication.setSubscribed(false);
				logger.debug('Track unsubscribed', {participantIdentity, trackSid: publication.trackSid});
			},
			'Failed to unsubscribe',
		);
	}

	private setPublicationEnabled(participantIdentity: string, enabled: boolean): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.ScreenShare,
				subscribed: true,
				enabled,
			});
			return;
		}
		this.withActiveScreenSharePublications(participantIdentity, (publications) => {
			this.applyToPublications(
				participantIdentity,
				publications,
				(publication) => {
					publication.setEnabled(enabled);
					logger.debug('Track enabled state updated', {participantIdentity, trackSid: publication.trackSid, enabled});
				},
				'Failed to set enabled',
			);
		});
	}

	private setPublicationQuality(participantIdentity: string, quality: VideoQualityLevel): void {
		if (this.nativeController) {
			this.nativeController.setRemoteTrackSubscription({
				participantIdentity,
				source: VoiceTrackSource.ScreenShare,
				subscribed: true,
				quality,
			});
			return;
		}
		this.withActiveScreenSharePublications(participantIdentity, (publications) => {
			for (const publication of publications) {
				this.applyQuality(publication, quality);
			}
			logger.debug('Quality updated', {participantIdentity, quality});
		});
	}
}
